/**
 * FPS / frame-time monitor with adaptive quality.
 */

function defaultQuality() {
    if (typeof navigator === 'undefined') return 'high';
    const mem = navigator.deviceMemory;
    const cores = navigator.hardwareConcurrency || 8;
    const coarse = typeof window !== 'undefined'
        && window.matchMedia?.('(pointer: coarse)').matches;
    if ((mem && mem <= 4) || cores <= 4 || coarse) return 'medium';
    return 'high';
}

export class PerfStats {
    constructor() {
        this.enabled = true;
        this.frames = 0;
        this.fps = 60;
        this.frameMs = 16.7;
        this.minFps = 60;
        this.maxFps = 0;
        this._lastTime = performance.now();
        this._accum = 0;
        this._fpsFrames = 0;

        this._samples = new Float32Array(90);
        this._sampleIdx = 0;
        this._sampleCount = 0;

        /** @type {'high'|'medium'|'low'} */
        this.quality = defaultQuality();
        this._qualityHold = 0; // frames to wait before adapting again

        this.counts = {
            enemies: 0,
            projectiles: 0,
            particles: 0,
            coins: 0,
            warps: 0,
            geoBombs: 0,
        };

        this.el = null;
        this._domAccum = 0;
        this.collapsed = false;
        this._userToggled = false;
        this._createDom();
    }

    _isCompact() {
        if (typeof window === 'undefined') return false;
        if (document.body.classList.contains('is-native')) return true;
        return window.matchMedia('(max-width: 768px)').matches;
    }

    syncCompact() {
        const compact = this._isCompact();
        if (this.el) this.el.classList.toggle('is-compact', compact);
        if (compact && !this._userToggled) this.collapsed = true;
        if (!compact) this.collapsed = false;
        this._renderDom();
    }

    _createDom() {
        if (typeof document === 'undefined') return;
        let el = document.getElementById('perfStats');
        if (!el) {
            el = document.createElement('div');
            el.id = 'perfStats';
            document.body.appendChild(el);
        }
        this.el = el;
        el.addEventListener('pointerdown', (e) => {
            if (!this._isCompact() || !this.enabled) return;
            e.stopPropagation();
            e.preventDefault();
            this._userToggled = true;
            this.collapsed = !this.collapsed;
            this._renderDom();
        });
        this.syncCompact();
        window.addEventListener('resize', () => this.syncCompact());
    }

    tick() {
        const now = performance.now();
        let dt = now - this._lastTime;
        if (dt > 100) dt = 100;
        if (dt < 0) dt = 0;
        this._lastTime = now;
        this.frameMs = dt;
        this.frames++;

        this._samples[this._sampleIdx] = dt;
        this._sampleIdx = (this._sampleIdx + 1) % this._samples.length;
        if (this._sampleCount < this._samples.length) this._sampleCount++;

        this._fpsFrames++;
        this._accum += dt;
        if (this._accum >= 400) {
            this.fps = Math.round((this._fpsFrames * 1000) / this._accum);
            this.minFps = Math.min(this.minFps, this.fps);
            this.maxFps = Math.max(this.maxFps, this.fps);
            this._fpsFrames = 0;
            this._accum = 0;
            this._adaptQuality();
        }

        this._domAccum += dt;
        if (this._domAccum >= 300) {
            this._domAccum = 0;
            this._renderDom();
        }

        return { dt, quality: this.quality };
    }

    avgFrameMs() {
        if (this._sampleCount === 0) return this.frameMs;
        let sum = 0;
        for (let i = 0; i < this._sampleCount; i++) sum += this._samples[i];
        return sum / this._sampleCount;
    }

    /** 1% low approximation from sample buffer */
    p1FrameMs() {
        if (this._sampleCount < 10) return this.avgFrameMs();
        const arr = Array.from(this._samples.subarray(0, this._sampleCount));
        arr.sort((a, b) => b - a); // worst first
        const i = Math.max(0, Math.floor(arr.length * 0.01));
        return arr[i];
    }

    _adaptQuality() {
        if (this._qualityHold > 0) {
            this._qualityHold--;
            return;
        }

        const fps = this.fps;
        let next = this.quality;

        // More forgiving thresholds + hold so we don't thrash into LOW forever
        if (fps < 38) next = 'low';
        else if (fps < 48) next = this.quality === 'high' ? 'medium' : this.quality;
        else if (fps >= 56 && this.quality !== 'high') next = this.quality === 'low' ? 'medium' : 'high';
        else if (fps >= 50 && this.quality === 'low') next = 'medium';

        if (next !== this.quality) {
            this.quality = next;
            this._qualityHold = 4; // ~1.6s at 400ms samples
        }
    }

    setCounts(counts) {
        Object.assign(this.counts, counts);
    }

    toggle() {
        this.enabled = !this.enabled;
        if (this.el) this.el.style.display = this.enabled ? 'block' : 'none';
    }

    resetMinMax() {
        this.minFps = this.fps;
        this.maxFps = this.fps;
    }

    maxParticles() {
        if (this.quality === 'low') return 80;
        if (this.quality === 'medium') return 140;
        return 220;
    }

    useShadows() {
        return this.quality === 'high';
    }

    drawExtras() {
        return this.quality !== 'low';
    }

    _statusLabel() {
        if (this.fps >= 55) return { text: 'SMOOTH', color: '#33ff99' };
        if (this.fps >= 45) return { text: 'OK', color: '#ffcc33' };
        if (this.fps >= 30) return { text: 'HEAVY', color: '#ff9944' };
        return { text: 'STUTTER', color: '#ff4466' };
    }

    _renderDom() {
        if (!this.el || !this.enabled) return;
        const compact = this._isCompact();
        this.el.classList.toggle('is-compact', compact);
        this.el.classList.toggle('collapsed', compact && this.collapsed);
        this.el.setAttribute('role', compact ? 'button' : 'status');
        this.el.setAttribute('aria-expanded', compact ? String(!this.collapsed) : 'true');

        const fpsColor = this.fps >= 55 ? '#33ff99' : this.fps >= 40 ? '#ffcc33' : '#ff5566';
        if (compact && this.collapsed) {
            this.el.innerHTML =
                `<div class="perf-chip">` +
                `<span class="perf-fps" style="color:${fpsColor}">${this.fps}</span>` +
                `<small> FPS</small>` +
                `</div>`;
            return;
        }

        const c = this.counts;
        const avg = this.avgFrameMs();
        const budget = 16.67;
        const budgetPct = Math.min(100, Math.round((avg / budget) * 100));
        const status = this._statusLabel();
        const qColor = this.quality === 'high' ? '#33ff99'
            : this.quality === 'medium' ? '#ffcc33' : '#ff5566';
        const barColor = budgetPct <= 100 ? '#33ff99' : budgetPct <= 140 ? '#ffcc33' : '#ff5566';
        const hint = compact
            ? 'Tap to collapse'
            : 'Target 60 FPS · F3 hide · F4 reset';

        this.el.innerHTML =
            `<div class="perf-head">` +
            `<span class="perf-status" style="color:${status.color}">${status.text}</span>` +
            `<span class="perf-fps" style="color:${fpsColor}">${this.fps}<small> FPS</small></span>` +
            `</div>` +
            `<div class="perf-budget">` +
            `<div class="perf-budget-fill" style="width:${Math.min(100, budgetPct)}%;background:${barColor}"></div>` +
            `</div>` +
            `<div class="perf-row"><span class="perf-label">FRAME</span>` +
            `<span class="perf-val">${avg.toFixed(1)} / 16.7ms</span></div>` +
            `<div class="perf-row"><span class="perf-label">1% LOW</span>` +
            `<span class="perf-val">${(1000 / Math.max(1, this.p1FrameMs())).toFixed(0)} fps</span></div>` +
            `<div class="perf-row"><span class="perf-label">MIN/MAX</span>` +
            `<span class="perf-val">${this.minFps}–${this.maxFps}</span></div>` +
            `<div class="perf-row"><span class="perf-label">QUALITY</span>` +
            `<span class="perf-val" style="color:${qColor}">${this.quality.toUpperCase()}</span></div>` +
            `<div class="perf-sep"></div>` +
            `<div class="perf-row dim"><span>enemies</span><span>${c.enemies}</span></div>` +
            `<div class="perf-row dim"><span>projectiles</span><span>${c.projectiles}</span></div>` +
            `<div class="perf-row dim"><span>particles</span><span>${c.particles}</span></div>` +
            `<div class="perf-row dim"><span>crystals</span><span>${c.coins}</span></div>` +
            `<div class="perf-row dim"><span>warps</span><span>${c.warps}</span></div>` +
            `<div class="perf-row dim"><span>bombs</span><span>${c.geoBombs}</span></div>` +
            `<div class="perf-hint">${hint}</div>`;
    }
}
